import { GridColDef } from "@mui/x-data-grid";
import { enumToStringConverter, formatDate, moneyFormat } from "../../../../helpers/appHelpers";
import useCompanyStore from "../../../../state-management/useCompanyStore";
import { getAllowanceActionColor } from "../data";
import { useGetImage } from "../../../../helpers/useGetImage";

type ReportTable = {
    title: string;
    tableData: any[];
    tableColumns: GridColDef[];
    type: "EMPLOYEE_ENGAGEMENT" 
        | "EMPLOYEE_BASIC_SALARY" 
        | "EMPLOYEE_JOB_TITLE" 
        | "EMPLOYEE_BAND" 
        | "EMPLOYEE_NOTCH" 
        | "EMPLOYEE_QUALIFICATION" 
        | "EMPLOYEE_ALLOWANCE" 
        | "EMPLOYEE_DISENGAGEMENT";
}

type EmployeeReportPDFProps = {
    reportTables: ReportTable[];
    employeeName?: string;
}

const EmployeeReportPDF = ({ reportTables, employeeName }: EmployeeReportPDFProps) => {
	const { currentCompany } = useCompanyStore();
    const companyLogo = useGetImage(currentCompany?.logoUrl || "", [currentCompany]);

    return (
        <div id="pdf-content" className="fixed -top-[300%] -left-[500%] z-[0] bg-white px-[80px] py-[50px] w-dvw mx-auto text-black whitespace-nowrap">
            <div className="w-full flex items-center justify-between gap-[600px] mb-[50px]">
                <div className="flex items-center gap-2">
                    {companyLogo && (
                        <img 
                            src={companyLogo}
                            alt=""
                            className="h-[60px] w-auto rounded-lg object-cover"
                        />
                    )}
                    <h2 className="text-3xl font-bold mb-6">{currentCompany?.name}</h2>
                </div>
                <div className="text-center">
                    <p className="text-[18px]">Report:</p>
                    <p className="text-xl font-semibold">{employeeName}</p>
                </div>
                {/* <p className="text-[18px] font-semibold">{employeeName + "'s Report"}</p> */}
            </div>
            <div className="w-full flex flex-col gap-8">
                {reportTables.map((table) => (
                    <section key={table.type} className="w-full">
                        <h5 className="text-base font-semibold mb-3.5">{table.title}</h5>
                        <table className="w-full border-collapse border border-gray-300 text-sm">
                            <thead>
                                <tr className="bg-gray-200">
                                    <th key={table.type + "0"} className="border border-gray-300 py-2 px-5">No</th>
                                    {table.tableColumns?.map((column, index) => (
                                        <th 
                                            key={table.type + `${index} + 1`} 
                                            className="border border-gray-300 py-2 px-5"
                                        >
                                            {column.headerName}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {table.tableData?.map((row, index) => (
                                    <tr key={table.type + `${index} + 1`} className="hover:bg-gray-100">
                                        <td className="border border-gray-300 py-2 px-5">{index + 1}</td>
                                        {(() => {
                                            switch(table.type) {
                                                case "EMPLOYEE_ENGAGEMENT":
                                                    return (
                                                        <>
                                                            <td className="border border-gray-300 py-2 px-5">{formatDate(row?.recordedAt)}</td>
                                                            <td className="border border-gray-300 py-2 px-5">{row?.jobTitle?.name}</td>
                                                            <td className="border border-gray-300 py-2 px-5">{row?.contractType?.name}</td>
                                                            <td className="border border-gray-300 py-2 px-5">{row?.basicSalary ? `${row?.currency?.code || ""} ${moneyFormat(row?.basicSalary)}` : "--"}</td>
                                                        </>
                                                    );
                                                case "EMPLOYEE_BASIC_SALARY":
                                                    return (
                                                        <>
                                                            <td className="border border-gray-300 py-2 px-5">{formatDate(row?.doneAt)}</td>
                                                            <td className="border border-gray-300 py-2 px-5">{row?.previousValue ? `${row?.previousCurrency?.code || ""} ${moneyFormat(row?.previousValue)}` : "--"}</td>
                                                            <td className="border border-gray-300 py-2 px-5">{row?.currentValue ? `${row?.currentCurrency?.code || ""} ${moneyFormat(row?.currentValue)}` : "--"}</td>
                                                        </>
                                                    );
                                                case "EMPLOYEE_JOB_TITLE":
                                                    return (
                                                        <>
                                                            <td className="border border-gray-300 py-2 px-5">{formatDate(row?.doneAt)}</td>
                                                            <td className="border border-gray-300 py-2 px-5">{row?.previousValue?.name}</td>
                                                            <td className="border border-gray-300 py-2 px-5">{row?.currentValue?.name}</td>
                                                        </>
                                                    );
                                                case "EMPLOYEE_BAND":
                                                    return (
                                                        <>
                                                            <td className="border border-gray-300 py-2 px-5">{formatDate(row?.doneAt)}</td>
                                                            <td className="border border-gray-300 py-2 px-5">{row?.previousValue?.name}</td>
                                                            <td className="border border-gray-300 py-2 px-5">{row?.currentValue?.name}</td>
                                                        </>
                                                    );
                                                case "EMPLOYEE_NOTCH":
                                                    return (
                                                        <>
                                                            <td className="border border-gray-300 py-2 px-5">{formatDate(row?.doneAt)}</td>
                                                            <td className="border border-gray-300 py-2 px-5">{row?.previousValue?.code + (row?.previousValue?.employeeBand ? `(${row?.previousValue?.employeeBand?.name})` : "")}</td>
                                                            <td className="border border-gray-300 py-2 px-5">{row?.currentValue?.code + (row?.currentValue?.employeeBand ? `(${row?.currentValue?.employeeBand?.name})` : "")}</td>
                                                        </>
                                                    );
                                                case "EMPLOYEE_QUALIFICATION":
                                                    return (
                                                        <>
                                                            <td className="border border-gray-300 py-2 px-5">{formatDate(row?.doneAt)}</td>
                                                            <td className="border border-gray-300 py-2 px-5">{row?.previousValue?.qualification}</td>
                                                            <td className="border border-gray-300 py-2 px-5">{row?.currentValue?.qualification}</td>
                                                        </>
                                                    );
                                                case "EMPLOYEE_ALLOWANCE":
                                                    return (
                                                        <>
                                                            <td className="border border-gray-300 py-2 px-5">{formatDate(row?.doneAt)}</td>
                                                            <td className="border border-gray-300 py-2 px-5">{row?.previousValue?.name}</td>
                                                            <td className="border border-gray-300 py-2 px-5">{row?.currentValue?.name}</td>
                                                            <td className="border border-gray-300 py-2 px-5">
                                                                <div 
                                                                    className="rounded-lg px-4 py-2 min-w-[80px] text-center relative font-semibold overflow-hidden"
                                                                    style={{ color: getAllowanceActionColor(row?.action) }}
                                                                >
                                                                    <div 
                                                                        className="absolute inset-0" 
                                                                        style={{ 
                                                                            background: getAllowanceActionColor(row?.action), 
                                                                            opacity: "15%"
                                                                        }}
                                                                    />
                                                                    <p>{enumToStringConverter(row?.action)}</p>
                                                                </div>
                                                            </td>
                                                        </>
                                                    );
                                                case "EMPLOYEE_DISENGAGEMENT":
                                                    return (
                                                        <>
                                                            <td className="border border-gray-300 py-2 px-5">{formatDate(row?.recordedAt)}</td>
                                                            <td className="border border-gray-300 py-2 px-5">{row?.reason}</td>
                                                            <td className="border border-gray-300 py-2 px-5">{row?.jobTitle?.name}</td>
                                                            <td className="border border-gray-300 py-2 px-5">{row?.contractType?.name}</td>
                                                        </>
                                                    );
                                                default:
                                                    return null;
                                            }
                                        })()}
                                        <td className="border border-gray-300 py-2 px-5">{row?.actor?.name || "User"}</td>
                                    </tr>
                                ))}

                                {!table.tableData?.length ? (
                                    <tr className="hover:bg-gray-100 text-center">
                                        <td 
                                            colSpan={table.tableColumns.length} 
                                            className="border border-gray-300 py-2 px-5"
                                        >No Data
                                        </td>
                                    </tr>
                                ): null}
                            </tbody>
                        </table>
                    </section>
                ))}
            </div>
        </div>
    );
};

export default EmployeeReportPDF;